import React, { useState, useCallback } from 'react';
import { AlertTriangle, Check, ChevronRight, ChevronLeft, FileText, Loader2, PackageX, Truck } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import { useSubmitDeliveryIncompleteMutation } from '../../../store/slices/apiSlice';

interface IncompleteDeliveryFlowProps {
  jobId: string;
  onComplete: () => void;
  onCancel: () => void;
}

type IncompleteReason = 'manufacturer_fault' | 'delivery_fault' | '';

export const IncompleteDeliveryFlow: React.FC<IncompleteDeliveryFlowProps> = ({
  jobId,
  onComplete,
  onCancel,
}) => {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState<IncompleteReason>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const [submitIncomplete] = useSubmitDeliveryIncompleteMutation();

  const validateStep = useCallback((currentStep: number): boolean => {
    switch (currentStep) {
      case 1:
        if (!reason) {
          toast.error('Please select a reason', 'You must choose a reason for incomplete delivery');
          return false;
        }
        return true;
      case 2:
        if (notes.length < 5) {
          toast.error('Notes too short', 'Please provide more details (min 5 characters)');
          return false;
        }
        return true;
      default:
        return true;
    }
  }, [reason, notes, toast]);

  const handleNext = useCallback(() => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  }, [step, validateStep]);

  const handleBack = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onCancel();
    }
  }, [step, onCancel]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(2)) return;

    setSubmitting(true);
    
    try {
      await submitIncomplete({
        jobId,
        data: { reason: reason as 'manufacturer_fault' | 'delivery_fault', notes }
      }).unwrap();
      
      toast.success('Delivery marked as incomplete');
      onComplete();
    } catch (error: any) {
      const message = error?.data?.detail || error?.data?.error || 'Please try again';
      toast.error('Failed to save', message);
    } finally {
      setSubmitting(false);
    }
  }, [validateStep, jobId, reason, notes, submitIncomplete, onComplete, toast]);

  // Progress Bar
  const renderProgressBar = () => (
    <div className="flex items-center gap-2 mb-6">
      {[1, 2].map((s) => (
        <React.Fragment key={s}>
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all
            ${s === step ? 'bg-orange-600 text-white ring-4 ring-orange-100' : ''}
            ${s < step ? 'bg-orange-500 text-white' : ''}
            ${s > step ? 'bg-gray-200 text-gray-500' : ''}
          `}>
            {s < step ? <Check className="w-5 h-5" /> : s}
          </div>
          {s < 2 && (
            <div className={`
              flex-1 h-1.5 rounded-full transition-all
              ${s < step ? 'bg-orange-500' : 'bg-gray-200'}
            `} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Reason Selection
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <AlertTriangle className="w-8 h-8 text-orange-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Why is this delivery incomplete?</h3>
        <p className="text-gray-500 text-sm mt-1">Select the reason that best describes the issue</p>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => setReason('manufacturer_fault')}
          className={`
            w-full p-4 border-2 rounded-xl text-left transition-all
            ${reason === 'manufacturer_fault'
              ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
          `}
        >
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${reason === 'manufacturer_fault' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
              <PackageX className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Manufacturer Fault</div>
              <div className="text-sm text-gray-500 mt-1">
                Wrong size, damaged items, manufacturing defects, incorrect order
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => setReason('delivery_fault')}
          className={`
            w-full p-4 border-2 rounded-xl text-left transition-all
            ${reason === 'delivery_fault'
              ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
          `}
        >
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${reason === 'delivery_fault' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-gray-900">Delivery Fault</div>
              <div className="text-sm text-gray-500 mt-1">
                Could not access location, customer not available, wrong address, access issues
              </div>
            </div>
          </div>
        </button>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={handleBack}
          className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleNext}
          disabled={!reason}
          className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          Next
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  // Step 2: Notes
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <FileText className="w-8 h-8 text-orange-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Please describe the issue</h3>
        <p className="text-gray-500 text-sm mt-1">Provide details about why the delivery could not be completed</p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Customer reported wrong glass size, items were damaged during transport, etc."
          rows={6}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all resize-none"
          autoFocus
        />
        <div className="flex justify-between text-sm">
          {notes.length < 5 ? (
            <p className="text-red-500">Minimum 5 characters required</p>
          ) : (
            <span />
          )}
          <p className="text-gray-400">{notes.length} characters</p>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-orange-50 rounded-lg p-4 space-y-2">
        <h4 className="font-medium text-orange-900">Summary</h4>
        <div className="text-sm text-orange-800">
          <p>
            <span className="font-medium">Reason:</span>{' '}
            {reason === 'manufacturer_fault' ? 'Manufacturer Fault' : 'Delivery Fault'}
          </p>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => setStep(1)}
          className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={notes.length < 5 || submitting}
          className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <AlertTriangle className="w-5 h-5" />
              Mark as Incomplete
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl p-4">
      {renderProgressBar()}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
};
